import Component from '@glimmer/component';
import { tracked, cached } from '@glimmer/tracking';
import { action } from '@ember/object';
import { assert } from '@ember/debug';
import { isNone } from '@ember/utils';
import type Owner from '@ember/owner';
import envelopeFill from '../helpers/envelope-fill.ts';
import envelopeUpper from '../helpers/envelope-upper.ts';
import envelopeLower from '../helpers/envelope-lower.ts';
import functionLine from '../helpers/function-line.ts';
import mathAdd from '../helpers/math-add.ts';
import onMoveOutside from '../modifiers/on-move-outside.ts';
import { computeGeometry } from '../utils/graphUtils.ts';
import { getClosestDataX, getXValues, getYValues, getCircleCoordinates } from '../utils/hoverUtils.ts';

import type {
  MinigraphModel,
  GraphOptions,
  GraphGeometry,
  GraphCoordinate,
  GraphLine,
  CircleCoordinate,
  HoverTextValues,
  HoverTitles,
} from '../index.ts';

interface AditoolsMiniGraphArgs {
  model: MinigraphModel;
  options: GraphOptions;
  xTitleKey?: string;
  yTitleKey?: string;
  y2TitleKey?: string;
  width?: number;
  height?: number;
  leftMargin?: number;
  topMargin?: number;
  rightMargin?: number;
  bottomMargin?: number;
  lineClasses?: string[];
  leftLabelOffset?: number;
  rightLabelOffset?: number;
  bottomLabelOffset?: number;
  onHighlightedLinesChanged?: (highlightedLines: number[]) => void;
}

interface AditoolsMiniGraphSignature {
  Args: AditoolsMiniGraphArgs;
  Element: SVGSVGElement;
  Blocks: { default: [GraphGeometry, GraphGeometry?] };
}

export default class AditoolsMiniGraph extends Component<AditoolsMiniGraphSignature> {
  @tracked hoverClientX = 0;
  @tracked hoverClientY = 0;
  @tracked hoverReady = false;
  hoverBox: [number, number] = [0, 0];
  @tracked prevHighlightedLines: number[] = [];

  envelopeFill = envelopeFill;
  envelopeUpper = envelopeUpper;
  envelopeLower = envelopeLower;
  functionLine = functionLine;
  mathAdd = mathAdd;
  onMoveOutside = onMoveOutside;

  constructor(owner: Owner, args: AditoolsMiniGraphArgs) {
    super(owner, args);

    // Validate parameters
    assert('`model` is required', !isNone(this.args.model));
    assert('`options` is required', !isNone(this.args.options));
    assert('`model` requires a limits member', !isNone(this.args.model) && !isNone(this.args.model.limits));

    if (!isNone(this.args.model) && !isNone(this.args.model.limits)) {
      const limits = this.args.model.limits;
      assert(`model.limits.xmin contains non-numerical value ${limits.xmin}`, !isNaN(limits.xmin));
      assert(`model.limits.xmax contains non-numerical value ${limits.xmax}`, !isNaN(limits.xmax));
      assert(`model.limits.ymin contains non-numerical value ${limits.ymin}`, !isNaN(limits.ymin));
      assert(`model.limits.ymax contains non-numerical value ${limits.ymax}`, !isNaN(limits.ymax));
    }
  }

  // Optional parameters with defaults applied
  get leftMargin(): number {
    return (this.args.leftMargin ?? 5) + (this.hasYTitle ? this.leftLabelOffset : 0);
  }
  get rightMargin(): number {
    return (this.args.rightMargin ?? 5) + (this.hasY2Title ? this.rightLabelOffset : 0);
  }
  get topMargin(): number {
    return this.args.topMargin ?? 5;
  }
  get bottomMargin(): number {
    return (this.args.bottomMargin ?? 5) + (this.hasXTitle ? this.bottomLabelOffset : 0);
  }
  get leftLabelOffset(): number {
    return this.args.leftLabelOffset ?? 20;
  }
  get rightLabelOffset(): number {
    return this.args.rightLabelOffset ?? 20;
  }
  get bottomLabelOffset(): number {
    return this.args.bottomLabelOffset ?? 20;
  }

  get hoverTitles(): HoverTitles {
    if (this.args.options.hoverTitles) {
      return this.args.options.hoverTitles;
    } else {
      const graphs = this.args.model.data.length;
      return { headerLabel: '', lineLabels: Array(graphs).fill('') };
    }
  }

  @cached
  get lineClasses(): string[] {
    const model = this.args.model;
    const properLength = model.data.length + (model.dataRight ? model.dataRight.length : 0);
    if (this.args.lineClasses && this.args.lineClasses.length === properLength) {
      return this.args.lineClasses;
    } else {
      const realClasses = [];
      for (let index = 0; index < properLength; index++) {
        if (this.args.lineClasses && index < this.args.lineClasses.length && this.args.lineClasses[index]) {
          realClasses.push(this.args.lineClasses[index] as string); //Covered by guard clause above
        } else {
          realClasses.push('default-line-class');
        }
      }
      return realClasses;
    }
  }

  // Boolean properties for graph axis titles
  get hasXTitle(): boolean {
    return this.args.xTitleKey !== undefined && this.args.xTitleKey.length > 0;
  }
  get hasYTitle(): boolean {
    return this.args.yTitleKey !== undefined && this.args.yTitleKey.length > 0;
  }
  get hasY2Title(): boolean {
    return this.args.y2TitleKey !== undefined && this.args.y2TitleKey.length > 0;
  }

  get width(): number {
    const defaultValue = 220;
    const value = typeof this.args.width === 'undefined' ? defaultValue : this.args.width;
    return value;
  }
  get height(): number {
    const defaultValue = 150;
    const value = typeof this.args.height === 'undefined' ? defaultValue : this.args.height;
    return value;
  }

  // Derived values
  get chartWidth(): number {
    let value = this.width - (this.leftMargin + this.rightMargin);
    if (value < 0) value = 0;
    return value;
  }

  get chartHeight(): number {
    let value = this.height - (this.topMargin + this.bottomMargin);
    if (value < 0) value = 0;
    return value;
  }

  get geometry(): GraphGeometry {
    return computeGeometry(this.args.model.limits, this.args.options, this.chartWidth, this.chartHeight);
  }

  get geometryRight(): GraphGeometry {
    const options = {
      xscale: this.args.options.xscale,
      yscale: this.args.options.yscale2 || 'linear',
      xticks: this.args.options.xticks,
      yticks: this.args.options.yticks2 || 5,
      yformat: this.args.options.yformat2 || this.args.options.yformat,
    };
    return this.args.model.limitsRight
      ? computeGeometry(this.args.model.limitsRight, options, this.chartWidth, this.chartHeight)
      : this.geometry;
  }

  get isMultiline(): boolean {
    if (this.args.model.data[0]) {
      return typeof this.args.model.data[0][0] !== 'number';
    }
    return false;
  }

  get hasSecondaryYAxis(): boolean {
    if (this.args.options.yAxes === 2) {
      if (!this.args.model.dataRight || !this.args.model.limitsRight) {
        console.debug(
          'When yAxes is 2, model.dataRight and model.limitsRight must be supplied. Only left Y axis will be displayed.',
        );
      }
    } else if (typeof this.args.options.yAxes !== 'undefined' && this.args.options.yAxes !== 1) {
      console.debug('If supplied, options.yAxes must be 1 or 2. Only left Y axis will be displayed.');
    }
    return !!this.args.options.yAxes && this.args.options.yAxes === 2;
  }

  get xZero(): { x1: number; y1: number; x2: number; y2: number } {
    const geometry = this.geometry;
    const x1 = geometry.xScale(geometry.limits.xmin);
    const x2 = geometry.xScale(geometry.limits.xmax);
    const y = geometry.yScale(0);

    return { x1: x1, y1: y, x2: x2, y2: y };
  }

  get yAxis(): { x1: number; y1: number; x2: number; y2: number } {
    const geometry = this.geometry;
    const y1 = geometry.yScale(geometry.limits.ymin);
    const y2 = geometry.yScale(geometry.limits.ymax);
    const x = geometry.xScale(geometry.limits.xmin);

    return { x1: x, y1: y1, x2: x, y2: y2 };
  }

  get yAxis2(): { x1: number; y1: number; x2: number; y2: number } {
    const geometry = this.geometryRight;
    const y1 = geometry.yScale(geometry.limits.ymin);
    const y2 = geometry.yScale(geometry.limits.ymax);
    const x = geometry.xScale(geometry.limits.xmax);

    return { x1: x, y1: y1, x2: x, y2: y2 };
  }

  get xTitleCenter(): GraphCoordinate {
    return { x: this.chartWidth / 2, y: this.chartHeight + (11 * 4) / 3 }; // Font Size is 11pt (need to convert to px)
  }

  get yTitleCenter(): GraphCoordinate {
    return { x: -5, y: this.chartHeight / 2 };
  }

  get y2TitleCenter(): GraphCoordinate {
    return { x: this.chartWidth + (11 * 4) / 3, y: this.chartHeight / 2 }; // Font Size is 11pt (need to convert to px)
  }

  get hoverPosition(): { x: number; y: number } {
    return {
      x: Math.max(0, this.hoverClientX - this.hoverBox[0] / 2) - this.leftMargin,
      y: Math.max(0, this.hoverClientY - this.hoverBox[1]),
    };
  }

  get multilineData(): GraphLine[] {
    return this.isMultiline ? (this.args.model.data as GraphLine[]) : ([this.args.model.data] as GraphLine[]);
  }
  get singleLineData(): GraphLine {
    return this.multilineData[0] as GraphLine;
  }
  @cached
  get closestXValue(): number {
    return getClosestDataX(
      this.hoverClientX - this.leftMargin,
      this.multilineData,
      this.geometry,
      this.args.model.dataRight,
    );
  }

  get circleCoordinates(): CircleCoordinate[] {
    return getCircleCoordinates(
      this.closestXValue,
      this.args.options,
      this.geometry,
      this.multilineData,
      this.args.model.dataRight,
      this.geometryRight,
    );
  }

  get hoverText(): HoverTextValues {
    const { formattedValue, formattedValueWithUnit } = getXValues(this.closestXValue, this.args.options);
    const yValues = getYValues(
      this.closestXValue,
      this.args.options,
      [this.geometry.xScale(this.closestXValue), this.hoverClientY - this.topMargin],
      this.multilineData,
      this.geometry,
      this.args.model.dataRight,
      this.geometryRight,
    );
    return {
      xAxisValueWithUnit: formattedValueWithUnit,
      xAxisValue: formattedValue,
      yAxisValues: yValues,
    };
  }

  get highlightedLines(): number[] {
    if (this.hoverText.yAxisValues.find((y) => y.focused == true)) {
      const indexLines: number[] = this.hoverText.yAxisValues
        .filter((y) => y.focused == true)
        .map((y) => y.index)
        .sort(function (a, b) {
          return a - b;
        });
      return indexLines;
    }
    return [];
  }

  @action updateHover(event: MouseEvent): void {
    if (this.args.options.hover) {
      const target = <HTMLInputElement>event.target;
      if (event.target && target.parentElement) {
        this.hoverClientX = event.offsetX;
        this.hoverClientY = event.offsetY;
        this.hoverReady = true;

        if (this.args.onHighlightedLinesChanged) {
          const a = performance.now();
          if (this.prevHighlightedLines.length != this.highlightedLines.length) {
            this.args.onHighlightedLinesChanged(this.highlightedLines);
            this.prevHighlightedLines = [...this.highlightedLines];

            const b = performance.now();
            if (b - a > 20) {
              console.log(`Long execution time: It took ${b - a} ms to highlight line changes`);
            }
          } else {
            const containsAll = this.prevHighlightedLines.every((element) => {
              return this.highlightedLines?.indexOf(element) !== -1;
            });
            if (!containsAll) {
              this.args.onHighlightedLinesChanged(this.highlightedLines);
              this.prevHighlightedLines = [...this.highlightedLines];

              const b = performance.now();
              if (b - a > 20) {
                console.log(`Long execution time: It took ${b - a} ms to highlight line changes`);
              }
            }
          }
        }
      }
    }
  }

  @action endHover(target: Element): void {
    let isSummary = false;
    for (let i = 0; i < target.classList.length; i++) {
      if (target.classList[i] === 'graph-hover_summary-line') {
        isSummary = true;
        break;
      }
    }
    if (this.hoverReady && !isSummary) {
      this.hoverReady = false;
    }
  }

  get showHover(): boolean {
    return !!this.hoverReady;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    AditoolsMiniGraph: typeof AditoolsMiniGraph;
  }
}
